/**
 * Palette Extraction — ported from SPA implementation.
 *
 * Provides k-means and median-cut clustering algorithms for extracting
 * dominant colors from a list of RGB pixel values.
 *
 * Browser-specific methods (extractFromImage, extractFromCamera) are
 * NOT included — those remain in the SPA. This module operates on
 * raw RGB arrays.
 */
// ── Helpers ─────────────────────────────────────────────────────────────────
function colorDistance(c1, c2) {
    return Math.sqrt((c1.r - c2.r) ** 2 + (c1.g - c2.g) ** 2 + (c1.b - c2.b) ** 2);
}
function rgbToHex(color) {
    const toHex = (n) => {
        const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
}
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
        : { r: 0, g: 0, b: 0 };
}
function rgbToHsl(rgb) {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min)
        return { h: 0, s: 0, l: l * 100 };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r)
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g)
        h = ((b - r) / d + 2) / 6;
    else
        h = ((r - g) / d + 4) / 6;
    return { h: h * 360, s: s * 100, l: l * 100 };
}
function calculateRelativeLuminance(rgb) {
    const rsRGB = rgb.r / 255;
    const gsRGB = rgb.g / 255;
    const bsRGB = rgb.b / 255;
    const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function calculateContrastRatio(c1, c2) {
    const l1 = calculateRelativeLuminance(c1);
    const l2 = calculateRelativeLuminance(c2);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}
function getAverageColor(colors) {
    if (colors.length === 0)
        return { r: 0, g: 0, b: 0 };
    const sum = colors.reduce((acc, c) => ({ r: acc.r + c.r, g: acc.g + c.g, b: acc.b + c.b }), { r: 0, g: 0, b: 0 });
    return { r: Math.round(sum.r / colors.length), g: Math.round(sum.g / colors.length), b: Math.round(sum.b / colors.length) };
}
function getRange(colors, channel) {
    const values = colors.map((c) => c[channel]);
    return Math.max(...values) - Math.min(...values);
}
// ── Algorithms ──────────────────────────────────────────────────────────────
/**
 * K-means clustering on RGB pixels.
 */
export function kMeansClustering(pixels, k, maxIterations = 50) {
    if (pixels.length === 0)
        return [];
    let centroids = [];
    for (let i = 0; i < k; i++) {
        centroids.push(pixels[Math.floor(Math.random() * pixels.length)]);
    }
    for (let iter = 0; iter < maxIterations; iter++) {
        const clusters = Array.from({ length: k }, () => []);
        for (const pixel of pixels) {
            let minDist = Infinity;
            let closestIndex = 0;
            for (let i = 0; i < k; i++) {
                const dist = colorDistance(pixel, centroids[i]);
                if (dist < minDist) {
                    minDist = dist;
                    closestIndex = i;
                }
            }
            clusters[closestIndex].push(pixel);
        }
        const newCentroids = [];
        for (let i = 0; i < k; i++) {
            newCentroids.push(clusters[i].length === 0 ? centroids[i] : getAverageColor(clusters[i]));
        }
        const converged = newCentroids.every((c, i) => c.r === centroids[i].r && c.g === centroids[i].g && c.b === centroids[i].b);
        centroids = newCentroids;
        if (converged)
            break;
    }
    return centroids;
}
/**
 * Median-cut quantization on RGB pixels.
 */
export function medianCutClustering(pixels, k) {
    if (pixels.length === 0)
        return [];
    const buckets = [pixels];
    while (buckets.length < k) {
        const largestBucket = buckets.reduce((max, b) => (b.length > max.length ? b : max));
        const bucketIndex = buckets.indexOf(largestBucket);
        if (largestBucket.length <= 1)
            break;
        const ranges = { r: getRange(largestBucket, 'r'), g: getRange(largestBucket, 'g'), b: getRange(largestBucket, 'b') };
        const largestChannel = Object.keys(ranges).reduce((max, ch) => (ranges[ch] > ranges[max] ? ch : max));
        largestBucket.sort((a, b) => a[largestChannel] - b[largestChannel]);
        const median = Math.floor(largestBucket.length / 2);
        buckets.splice(bucketIndex, 1, largestBucket.slice(0, median), largestBucket.slice(median));
    }
    return buckets.map(getAverageColor);
}
// ── Public API ──────────────────────────────────────────────────────────────
/**
 * Extract dominant colors from raw RGB pixel data.
 */
export function extractDominantColors(pixels, k = 5, algorithm = 'kmeans', brandSafeOptions) {
    if (pixels.length === 0)
        return [];
    const colors = algorithm === 'kmeans'
        ? kMeansClustering(pixels, k)
        : medianCutClustering(pixels, k);
    let hexColors = colors.map(rgbToHex);
    if (brandSafeOptions) {
        hexColors = applyBrandSafeFilters(hexColors, brandSafeOptions);
    }
    return hexColors;
}
/**
 * Apply brand-safe filtering to extracted colors.
 */
export function applyBrandSafeFilters(hexColors, options) {
    let filtered = [...hexColors];
    // Lightness filter
    if (options.minLightness !== undefined || options.maxLightness !== undefined) {
        filtered = filtered.filter((hex) => {
            const hsl = rgbToHsl(hexToRgb(hex));
            if (options.minLightness !== undefined && hsl.l < options.minLightness)
                return false;
            if (options.maxLightness !== undefined && hsl.l > options.maxLightness)
                return false;
            return true;
        });
    }
    // Saturation filter
    if (options.minSaturation !== undefined) {
        const minSat = options.minSaturation;
        filtered = filtered.filter((hex) => rgbToHsl(hexToRgb(hex)).s >= minSat);
    }
    // Outlier removal
    if (options.removeOutliers && filtered.length > 2) {
        const threshold = options.outlierThreshold ?? 50;
        const rgbColors = filtered.map((hex) => ({ hex, rgb: hexToRgb(hex) }));
        const avgRgb = getAverageColor(rgbColors.map((c) => c.rgb));
        const nonOutliers = rgbColors.filter((c) => colorDistance(c.rgb, avgRgb) <= threshold);
        if (nonOutliers.length >= 2)
            filtered = nonOutliers.map((c) => c.hex);
    }
    // Contrast bias
    if (options.contrastBias && options.targetBackgroundHex && filtered.length > 1) {
        const bgRgb = hexToRgb(options.targetBackgroundHex);
        filtered.sort((a, b) => calculateContrastRatio(hexToRgb(b), bgRgb) - calculateContrastRatio(hexToRgb(a), bgRgb));
    }
    return filtered;
}
//# sourceMappingURL=paletteExtraction.js.map